import {
  DndContext,
  type DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  Building2,
  Check,
  ChevronDown,
  ChevronUp,
  Copy,
  Folder,
  FolderPlus,
  GripVertical,
  Lock,
  Plus,
  RotateCw,
  Trash2,
  UserRound,
  Users,
} from "lucide-react";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { PageHeader } from "@/components/common/page-header";
import { getClientStats } from "@/features/clients/api";
import type { ClientStats } from "@/features/clients/types";
import { TabShell } from "@/features/settings/components/tab-shell";
import {
  applyFolderTemplate,
  type FolderTemplate,
  type FolderTemplateNode,
  updateFirmSettings,
} from "@/features/settings/firm-api";
import { useFirmSettings } from "@/features/settings/hooks/use-firm-settings";
import { ApiError } from "@/lib/api";
import { cn } from "@/lib/utils";

// The 4 system folders every client gets automatically (locked, not in the template).
const SYSTEM_FOLDERS = ["Forms", "Letters", "Notices", "Client Documents"];

type ClientType = "individual" | "business";

// The wire shape (FolderTemplateNode) has no id, but the editor reorders/deletes
// rows, so each node carries an ephemeral local id purely for a stable React key
// (stripped before saving). Without it, keying on array index drops input focus
// mid-edit when a sibling is moved or removed.
interface EditNode {
  id: string;
  name: string;
  children: EditNode[];
}

interface EditTree {
  individual: EditNode[];
  business: EditNode[];
}

let uidCounter = 0;
function uid(): string {
  uidCounter += 1;
  return `n${uidCounter}`;
}

function toEditNodes(nodes: FolderTemplateNode[]): EditNode[] {
  return nodes.map((n) => ({ id: uid(), name: n.name, children: toEditNodes(n.children) }));
}

function stripIds(nodes: EditNode[]): FolderTemplateNode[] {
  return nodes.map((n) => ({ name: n.name, children: stripIds(n.children) }));
}

/** Deep-clone a tree with fresh local ids (used by "Copy to …"). */
function cloneNodes(nodes: EditNode[]): EditNode[] {
  return nodes.map((n) => ({ id: uid(), name: n.name, children: cloneNodes(n.children) }));
}

function newNode(): EditNode {
  return { id: uid(), name: "New Folder", children: [] };
}

/** Count every descendant across all depths (the "N subfolders" figure). */
function countDescendants(nodes: EditNode[]): number {
  return nodes.reduce((acc, n) => acc + 1 + countDescendants(n.children), 0);
}

/** One draggable/sortable folder row. */
function FolderRow({
  node,
  index,
  count,
  disabled,
  depth,
  variant,
  onReplace,
  onAddChild,
  onMove,
  onRemove,
  childNodes,
}: {
  node: EditNode;
  index: number;
  count: number;
  disabled: boolean;
  depth: number;
  variant: ClientType;
  onReplace: (node: EditNode) => void;
  onAddChild: () => void;
  onMove: (delta: number) => void;
  onRemove: () => void;
  childNodes: EditNode[];
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: node.id,
    disabled,
  });
  const style = { transform: CSS.Transform.toString(transform), transition };
  const hasChildren = childNodes.length > 0;
  const iconColor =
    depth === 0
      ? variant === "individual"
        ? "text-accent-foreground"
        : "text-warning"
      : "text-muted-foreground";

  return (
    <li ref={setNodeRef} style={style} className={cn(isDragging && "relative z-10 opacity-80")}>
      <div className="group hover:bg-muted/40 hover:border-border flex items-center gap-2 rounded-sm border border-transparent px-1.5 py-1 transition-colors">
        <button
          type="button"
          aria-label="Drag to reorder"
          disabled={disabled}
          className="text-border hover:text-muted-foreground flex w-5 shrink-0 cursor-grab touch-none justify-center disabled:cursor-not-allowed disabled:opacity-50"
          {...attributes}
          {...listeners}
        >
          <GripVertical className="size-4" />
        </button>
        <Folder className={cn("size-[18px] shrink-0", iconColor)} />
        <Input
          value={node.name}
          disabled={disabled}
          maxLength={120}
          aria-label={depth === 0 ? "Folder name" : "Subfolder name"}
          onChange={(e) => onReplace({ ...node, name: e.target.value })}
          className={cn(
            "h-9 flex-1 border-transparent bg-transparent px-2.5 shadow-none",
            depth === 0 ? "font-semibold" : "font-medium",
          )}
        />
        {hasChildren && (
          <span className="bg-muted text-muted-foreground rounded-full px-2 py-0.5 text-[11px] whitespace-nowrap">
            {childNodes.length} inside
          </span>
        )}
        <span className="flex gap-0.5 opacity-0 transition-opacity group-focus-within:opacity-100 group-hover:opacity-100">
          {depth === 0 && (
            <Button
              type="button"
              variant="ghost"
              size="icon-sm"
              disabled={disabled}
              aria-label="Add subfolder"
              onClick={onAddChild}
            >
              <FolderPlus className="size-4" />
            </Button>
          )}
          <Button
            type="button"
            variant="ghost"
            size="icon-sm"
            disabled={disabled || index === 0}
            aria-label="Move up"
            onClick={() => onMove(-1)}
          >
            <ChevronUp className="size-4" />
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="icon-sm"
            disabled={disabled || index === count - 1}
            aria-label="Move down"
            onClick={() => onMove(1)}
          >
            <ChevronDown className="size-4" />
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="icon-sm"
            className="text-destructive"
            disabled={disabled}
            aria-label={depth === 0 ? "Delete folder" : "Delete subfolder"}
            onClick={onRemove}
          >
            <Trash2 className="size-4" />
          </Button>
        </span>
      </div>
      {hasChildren && (
        <div className="border-border mt-1 ml-[30px] border-l-2 pl-3">
          <FolderLevel
            nodes={childNodes}
            disabled={disabled}
            depth={depth + 1}
            variant={variant}
            onChange={(next) => onReplace({ ...node, children: next })}
          />
        </div>
      )}
    </li>
  );
}

/** Recursive editor for one level of a folder tree. `onChange` replaces this level. */
function FolderLevel({
  nodes,
  disabled,
  depth,
  variant,
  onChange,
}: {
  nodes: EditNode[];
  disabled: boolean;
  depth: number;
  variant: ClientType;
  onChange: (next: EditNode[]) => void;
}) {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const replace = (index: number, node: EditNode) =>
    onChange(nodes.map((n, i) => (i === index ? node : n)));
  const remove = (index: number) => onChange(nodes.filter((_, i) => i !== index));
  const move = (index: number, delta: number) => {
    const target = index + delta;
    if (target < 0 || target >= nodes.length) return;
    const next = [...nodes];
    [next[index], next[target]] = [next[target]!, next[index]!];
    onChange(next);
  };

  function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const from = nodes.findIndex((n) => n.id === active.id);
    const to = nodes.findIndex((n) => n.id === over.id);
    if (from < 0 || to < 0) return;
    const next = [...nodes];
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved!);
    onChange(next);
  }

  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
      <SortableContext items={nodes.map((n) => n.id)} strategy={verticalListSortingStrategy}>
        <ol className="flex list-none flex-col gap-1">
          {nodes.map((node, index) => (
            <FolderRow
              key={node.id}
              node={node}
              index={index}
              count={nodes.length}
              disabled={disabled}
              depth={depth}
              variant={variant}
              onReplace={(next) => replace(index, next)}
              onAddChild={() => replace(index, { ...node, children: [...node.children, newNode()] })}
              onMove={(delta) => move(index, delta)}
              onRemove={() => remove(index)}
              childNodes={node.children}
            />
          ))}
        </ol>
      </SortableContext>
      {depth > 0 && (
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={disabled}
          onClick={() => onChange([...nodes, newNode()])}
          className="text-muted-foreground mt-1 ml-[26px] border-dashed text-xs"
        >
          <Plus className="size-3" /> Add subfolder
        </Button>
      )}
    </DndContext>
  );
}

/** One client-type folder structure (Individuals or Businesses). */
function FolderCard({
  variant,
  title,
  nodes,
  disabled,
  clientCount,
  onChange,
  onCopy,
}: {
  variant: ClientType;
  title: string;
  nodes: EditNode[];
  disabled: boolean;
  clientCount: number | null;
  onChange: (next: EditNode[]) => void;
  onCopy: () => void;
}) {
  const subfolders = nodes.reduce((acc, n) => acc + countDescendants(n.children), 0);
  const Icon = variant === "individual" ? UserRound : Building2;
  const copyLabel = variant === "individual" ? "Copy to Businesses" : "Copy to Individuals";

  const appliesTo =
    clientCount === null
      ? null
      : clientCount === 1
        ? "applies to 1 client"
        : `applies to ${clientCount} clients${clientCount === 0 ? " yet" : ""}`;

  return (
    <Card className="gap-0 overflow-hidden p-0">
      <div className="flex items-center justify-between gap-3 border-b p-5">
        <div className="flex items-center gap-3">
          <span
            className={cn(
              "flex size-10 shrink-0 items-center justify-center rounded-sm",
              variant === "individual"
                ? "bg-accent text-accent-foreground"
                : "bg-warning-soft text-warning",
            )}
          >
            <Icon className="size-[18px]" />
          </span>
          <div className="flex flex-col">
            <h3 className="text-lg font-semibold tracking-tight">{title}</h3>
            <span className="text-muted-foreground text-xs">
              {nodes.length} folders · {subfolders} subfolders
              {appliesTo && (
                <>
                  {" · "}
                  {appliesTo}
                </>
              )}
            </span>
          </div>
        </div>
        <Button type="button" variant="outline" size="sm" disabled={disabled} onClick={onCopy}>
          <Copy className="size-3.5" /> {copyLabel}
        </Button>
      </div>

      <div className="p-3.5">
        <FolderLevel
          nodes={nodes}
          disabled={disabled}
          depth={0}
          variant={variant}
          onChange={onChange}
        />
      </div>

      <div className="px-3.5 pb-3.5">
        <Button
          type="button"
          variant="outline"
          disabled={disabled}
          onClick={() => onChange([...nodes, newNode()])}
          className="text-info hover:text-info w-full border-dashed"
        >
          <Plus className="size-4" /> Add folder
        </Button>
      </div>
    </Card>
  );
}

export function FoldersTab() {
  const { settings, setSettings, loading, error, isAdmin } = useFirmSettings();
  const [tree, setTree] = useState<EditTree>({ individual: [], business: [] });
  const [stats, setStats] = useState<ClientStats | null>(null);
  const [saving, setSaving] = useState(false);
  const [applying, setApplying] = useState(false);

  useEffect(() => {
    if (settings) {
      setTree({
        individual: toEditNodes(settings.folderTemplate.individual),
        business: toEditNodes(settings.folderTemplate.business),
      });
    }
  }, [settings]);

  useEffect(() => {
    let active = true;
    getClientStats()
      .then((s) => active && setStats(s))
      .catch(() => active && setStats(null));
    return () => {
      active = false;
    };
  }, []);

  const indDirty =
    !!settings &&
    JSON.stringify(stripIds(tree.individual)) !== JSON.stringify(settings.folderTemplate.individual);
  const bizDirty =
    !!settings &&
    JSON.stringify(stripIds(tree.business)) !== JSON.stringify(settings.folderTemplate.business);
  const dirty = indDirty || bizDirty;
  const changedLabel =
    indDirty && bizDirty
      ? "Both structures"
      : indDirty
        ? "Individuals structure"
        : bizDirty
          ? "Businesses structure"
          : "";

  async function handleSave() {
    setSaving(true);
    try {
      const folderTemplate: FolderTemplate = {
        individual: stripIds(tree.individual),
        business: stripIds(tree.business),
      };
      const updated = await updateFirmSettings({ folderTemplate });
      setSettings(updated);
      toast.success("Folder structure saved");
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Couldn't save folder structure.");
    } finally {
      setSaving(false);
    }
  }

  function handleRestoreDefaults() {
    if (settings) {
      setTree({
        individual: toEditNodes(settings.defaultFolderTemplate.individual),
        business: toEditNodes(settings.defaultFolderTemplate.business),
      });
    }
    toast.message("Restored the default structure — Save to apply.");
  }

  function handleDiscard() {
    if (settings) {
      setTree({
        individual: toEditNodes(settings.folderTemplate.individual),
        business: toEditNodes(settings.folderTemplate.business),
      });
    }
  }

  function handleCopy(from: ClientType) {
    const to: ClientType = from === "individual" ? "business" : "individual";
    setTree((t) => ({ ...t, [to]: cloneNodes(t[from]) }));
    toast.message(
      `Copied to ${to === "individual" ? "Individuals" : "Businesses"} — Save to apply.`,
    );
  }

  async function handleApplyAll() {
    setApplying(true);
    try {
      const result = await applyFolderTemplate();
      toast.success(result.message ?? "Applying to all clients…");
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Couldn't apply to clients.");
    } finally {
      setApplying(false);
    }
  }

  const applyLabel =
    stats === null
      ? "Apply to existing clients"
      : `Apply to ${stats.total} existing client${stats.total === 1 ? "" : "s"}`;

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title="Folder structure"
        subtitle="The folders every new client starts with, on top of the four system folders. Individuals and businesses each get their own layout — rename, nest and reorder as you like."
        actions={
          isAdmin ? (
            <Button type="button" variant="outline" onClick={handleRestoreDefaults}>
              <RotateCw className="size-4" /> Restore defaults
            </Button>
          ) : undefined
        }
      />
      <TabShell loading={loading} error={error}>
        {settings && (
          <div className="flex flex-col gap-6">
            {/* System folders strip */}
            <div className="bg-card flex flex-wrap items-center gap-x-4 gap-y-2.5 rounded-xl border px-4.5 py-3.5">
              <span className="text-muted-foreground inline-flex items-center gap-2 text-xs font-semibold tracking-[0.08em] uppercase">
                <Lock className="size-3.5" /> System folders · always created
              </span>
              <div className="flex flex-wrap gap-1.5">
                {SYSTEM_FOLDERS.map((name) => (
                  <span
                    key={name}
                    className="bg-muted text-muted-foreground inline-flex h-[30px] items-center gap-1.5 rounded-sm px-2.5 text-[13px] font-medium"
                  >
                    <Folder className="size-3.5" />
                    {name}
                  </span>
                ))}
              </div>
              <span className="text-muted-foreground ml-auto text-xs">
                Generated letters, notices and portal uploads file themselves here automatically.
              </span>
            </div>

            {/* Two structures */}
            <div className="grid items-start gap-6 lg:grid-cols-2">
              <FolderCard
                variant="individual"
                title="Individuals"
                nodes={tree.individual}
                disabled={!isAdmin}
                clientCount={stats?.individuals ?? null}
                onChange={(next) => setTree((t) => ({ ...t, individual: next }))}
                onCopy={() => handleCopy("individual")}
              />
              <FolderCard
                variant="business"
                title="Businesses"
                nodes={tree.business}
                disabled={!isAdmin}
                clientCount={stats?.businesses ?? null}
                onChange={(next) => setTree((t) => ({ ...t, business: next }))}
                onCopy={() => handleCopy("business")}
              />
            </div>

            {/* Apply + save */}
            {isAdmin ? (
              <div className="bg-card shadow-card flex flex-wrap items-center justify-between gap-3 rounded-xl border py-3.5 pr-4.5 pl-5.5">
                <div className="flex flex-1 basis-80 flex-col gap-0.5">
                  {dirty ? (
                    <span className="text-muted-foreground inline-flex items-center gap-2.5 text-[13px]">
                      <span className="bg-primary size-2 rounded-full" />
                      <strong className="text-foreground font-semibold">Unsaved changes</strong> ·{" "}
                      {changedLabel}
                    </span>
                  ) : (
                    <span className="text-muted-foreground inline-flex items-center gap-2.5 text-[13px]">
                      <span className="bg-success size-2 rounded-full" /> All changes saved
                    </span>
                  )}
                  <span className="text-muted-foreground text-xs">
                    New clients always get the saved structure. “Apply to existing” adds any missing
                    folders to every current client — nothing is deleted or moved.
                  </span>
                </div>
                <div className="flex flex-wrap gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleDiscard}
                    disabled={!dirty}
                  >
                    Discard
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleApplyAll}
                    disabled={applying}
                  >
                    <Users className="size-4" /> {applying ? "Applying…" : applyLabel}
                  </Button>
                  <Button
                    type="button"
                    variant="cta"
                    onClick={handleSave}
                    disabled={saving || !dirty}
                  >
                    <Check className="size-4" /> {saving ? "Saving…" : "Save changes"}
                  </Button>
                </div>
              </div>
            ) : (
              <p className="text-muted-foreground text-sm">
                Only a country director can edit the folder structure.
              </p>
            )}
          </div>
        )}
      </TabShell>
    </div>
  );
}
